import React from 'react';
import { Button } from '@/components/ui/button.tsx';
import { RowSelectionState, Table } from '@tanstack/react-table';
import { TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { useTranslation } from 'react-i18next';

export const DeviceTablePagination = ({
  table,
  hasSelectedRow,
  setSelectedRows,
}: {
  table: Table<TTreeDevice>;
  hasSelectedRow: boolean;
  setSelectedRows: undefined | React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center justify-between gap-4 px-2 pt-4 sm:flex-row sm:gap-2">
      <div className="text-muted-foreground flex-1 text-sm">
        {setSelectedRows && (
          <div className="text-muted-foreground flex-1 text-sm">
            {hasSelectedRow ? '1' : 0} {t('common.page.of')}{' '}
            {table.getFilteredRowModel()?.rows.length} {t('common.page.selectedRows')}
          </div>
        )}
        {!setSelectedRows && (
          <div className="text-muted-foreground flex-1 text-sm">
            {table.getFilteredRowModel().rows.length} {t('common.page.rows')}
          </div>
        )}
      </div>
      <div className="flex flex-col items-center gap-4 space-x-6 sm:flex-row sm:gap-0 lg:space-x-8">
        <div className="order-2 flex items-center space-x-2 sm:order-1">
          <p className="text-sm font-medium">{t('common.page.rowsPerPage')}</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-row gap-2 sm:order-2">
          <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
            {t('common.page.page', {
              current: table.getState().pagination.pageIndex + 1,
              total: table.getPageCount(),
            })}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              type="button"
            >
              {t('common.page.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              type="button"
            >
              {t('common.page.next')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
